import React, { ReactNode } from 'react'
import { v4 as uuid } from 'uuid'
import { Post } from '../../components/feed/Post'
import { ErrorView } from '../error/ErrorView'
import { InfiniteScroll } from '../../components/common/InfiniteScroll'
import { useAppTranslation } from '../../hooks/useAppTranslation'
import { PostRow } from '../../generated/api/post-row'
import { IllustrationChoices } from '../../components/common/Illustration'
import { ButtonVariant } from '../../components/common/Button'

type NewFeedProps = {
  posts: PostRow[]
  userCanPost: boolean
  filterActive: boolean
  isFetching: boolean
  openCreatePostModal: () => void
  fetchNextPage: () => void
}
const Feed = ({
  userCanPost,
  posts,
  filterActive,
  openCreatePostModal,
  isFetching,
  fetchNextPage,
}: NewFeedProps): ReactNode => {
  const { translate } = useAppTranslation()

  const noFilterMatch = filterActive && !posts.length
  if (noFilterMatch) {
    return (
      <ErrorView
        className="mt-14 gap-6 md:mt-28 md:gap-10"
        title={translate('feedView.emptyFilter.title')}
        subtitle={translate('feedView.emptyFilter.subtitle')}
        illustration={IllustrationChoices.EMPTY}
      />
    )
  }

  const feedEmpty = !posts.length
  const emptyFeedErrorView = {
    title: translate('feedView.emptyState.emptyFeed.title'),
    subtitle: translate('feedView.emptyState.emptyFeed.subtitle'),
    illustration: IllustrationChoices.EMPTY,
    buttons: [
      ...(userCanPost
        ? [
            {
              id: uuid(),
              button: {
                label: translate('feedView.emptyState.emptyFeed.buttonTitle'),
                onClick: openCreatePostModal,
                variant: ButtonVariant.PRIMARY,
              },
            },
          ]
        : []),
    ],
  }
  if (feedEmpty) {
    return <ErrorView {...emptyFeedErrorView} className="mt-14" />
  }
  return (
    <InfiniteScroll
      key="infiniteFeedScroll"
      onEndReached={fetchNextPage}
      isFetching={isFetching}
    >
      {posts.map((post) => {
        return <Post key={post.id} row={post} />
      })}
    </InfiniteScroll>
  )
}

export { Feed }
